import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

interface FaqItem { q: string; a: string }

interface FAQSectionProps {
  /**
   * Optional i18n section key (e.g. "aiAssistant", "reports").
   * If set, reads questions from `<key>.faq.items[]`.
   * If unset, falls back to the homepage's flat `homeFaq.q0..q6` keys.
   */
  sectionKey?: string;
  /** Optional heading override. */
  heading?: string;
  /** Optional heading line 2 override. */
  headingLine2?: string;
  /**
   * If true, emit FAQPage JSON-LD for this section. Default true
   * when sectionKey is set (per-page schema) and false otherwise
   * (the homepage FAQ is already in the sitewide schema graph).
   */
  emitJsonLd?: boolean;
}

const FAQSection = ({ sectionKey, heading, headingLine2, emitJsonLd }: FAQSectionProps = {}) => {
  const { t } = useTranslation();

  // Resolve FAQ items + heading
  let faqs: { question: string; answer: string }[] = [];
  let title = heading ?? t("homeFaq.title");
  let titleLine2 = headingLine2 ?? t("homeFaq.titleLine2");

  if (sectionKey) {
    // Per-page mode: read `<sectionKey>.faq.items` array
    const items = t(`${sectionKey}.faq.items`, { returnObjects: true });
    if (Array.isArray(items) && items.length > 0) {
      faqs = (items as FaqItem[]).map((it) => ({
        question: it.q,
        answer: it.a,
      }));
      // Use section-specific heading if available
      const sectionHeading = t(`${sectionKey}.faq.heading`, { defaultValue: "" });
      if (sectionHeading && typeof sectionHeading === "string") {
        title = heading ?? sectionHeading;
        titleLine2 = headingLine2 ?? "";
      }
    }
  }

  // Fallback to homeFaq if no items resolved
  if (faqs.length === 0) {
    faqs = [
      { question: t("homeFaq.q0"), answer: t("homeFaq.a0") },
      { question: t("homeFaq.q1"), answer: t("homeFaq.a1") },
      { question: t("homeFaq.q2"), answer: t("homeFaq.a2") },
      { question: t("homeFaq.q3"), answer: t("homeFaq.a3") },
      { question: t("homeFaq.q4"), answer: t("homeFaq.a4") },
      { question: t("homeFaq.q5"), answer: t("homeFaq.a5") },
      { question: t("homeFaq.q6"), answer: t("homeFaq.a6") },
    ].filter((f) => f.question && !f.question.startsWith("homeFaq."));
  }

  if (faqs.length === 0) return null;

  // Decide whether to emit JSON-LD: per-section pages get their own schema
  const shouldEmit = emitJsonLd ?? Boolean(sectionKey);
  const faqJsonLd = shouldEmit
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      }
    : null;

  return (
    <>
      {faqJsonLd && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        </Helmet>
      )}
      <section
        className="relative bg-background py-24 lg:py-32 overflow-hidden"
        aria-labelledby="faq-heading"
      >
        {/* Subtle dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
            backgroundSize: "32px 32px",
          }}
        />

        <div className="max-w-3xl mx-auto px-6 lg:px-12 relative">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="h-px w-12 bg-primary" />
              <span className="feature-descriptor text-primary text-xs">
                FAQ
              </span>
              <div className="h-px w-12 bg-primary" />
            </div>
            <h2
              id="faq-heading"
              className="feature-faq-heading mx-auto max-w-[44rem] text-4xl font-black leading-[0.95] text-foreground sm:text-5xl"
              style={{ fontFamily: "'Impact', 'Arial Black', sans-serif" }}
            >
              {title}
              {titleLine2 && (
                <>
                  <br />
                  {titleLine2}
                </>
              )}
            </h2>
          </div>

          {/* FAQ Accordion */}
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-border"
              >
                <AccordionTrigger className="feature-faq-question text-left text-foreground font-semibold text-base py-5 hover:no-underline hover:text-primary transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-[15px] leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
};

export default FAQSection;
